/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';

type ClickOutsideListenerOptions = {
    elements: Array<HTMLElement>;
    onOutsideClick: (ev: MouseEvent) => void;
    enabled: boolean;
};

export function useOutsideClickListener(
    options: ClickOutsideListenerOptions,
): void {
    const { elements = [], onOutsideClick: cb, enabled } = options;

    // Used to avoid dependencies in useEffect so that it is only dependent on the enable flag
    const elementsRef = useRef(elements);
    const safeCb = useRef<typeof cb>();

    useEffect(() => {
        safeCb.current = cb;
        elementsRef.current = elements;
    });

    useEffect(() => {
        function handleDocumentClick(ev: MouseEvent) {
            const target = ev.target as Node;
            const contained = elementsRef.current.find(el =>
                el?.contains(target),
            );
            if (!contained && enabled) {
                safeCb.current?.(ev);
                ev.preventDefault();
            }
        }
        if (!enabled) {
            return;
        }
        document.body.addEventListener('click', handleDocumentClick);
        return () =>
            document.body.removeEventListener('click', handleDocumentClick);
    }, [enabled]);
}

export type KeyEventListenerConfig = {
    onKeyPressed: (e: KeyboardEvent) => void;
    enabled: boolean;
    key?: string;
};

export function useKeyEventListener({
    onKeyPressed: cb,
    enabled,
    key = 'Escape',
}: KeyEventListenerConfig): void {
    const safeCb = useRef<typeof cb>();

    useEffect(() => {
        safeCb.current = cb;
    });

    useEffect(() => {
        function handleKeyPressed(e: KeyboardEvent) {
            if (e.key === key && enabled) {
                safeCb.current?.(e);
                e.preventDefault();
            }
        }

        if (!enabled) {
            return;
        }

        document.body.addEventListener('keyup', handleKeyPressed);
        return () =>
            document.body.removeEventListener('keyup', handleKeyPressed);
    }, [enabled, key]);
}

export function usePrevious<V>(value: V): V {
    const ref = useRef(value);

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
}

type AutoControlledOptions<V> = {
    defaultValue: V;
    initialValue?: V;
    value: V;
};

const isUndefined = (val: unknown) => typeof val === 'undefined';

export function useAutoControlled<TValue>(
    options: AutoControlledOptions<TValue>,
): [TValue, React.Dispatch<React.SetStateAction<TValue>>] {
    const [stateValue, setStateValue] = useState<TValue>(
        isUndefined(options.defaultValue)
            ? options.initialValue
            : options.defaultValue,
    );

    const value = isUndefined(options.value) ? stateValue : options.value;

    // Used to avoid dependencies in setState
    const valueRef = useRef<TValue>(value);

    const setValue = React.useCallback((newState: TValue) => {
        if (typeof newState === 'function') {
            valueRef.current = newState(valueRef.current);
        } else {
            valueRef.current = newState;
        }
        setStateValue(valueRef.current);
    }, []);

    return [value, setValue];
}
